import React, { FC, useState, useContext, useEffect } from 'react';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import OfflineWarningAlert from './OfflineWarningAlert';

import { backflowJobItemRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

import { BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';
import { patchDevice } from '../../utils/backflowCache';

interface BeginTestButtonProps {
  backflowJobItem: BackflowJobItem;
  onSuccess: () => void;
}

const BeginTestButton: FC<BeginTestButtonProps> = ({ backflowJobItem, onSuccess }) => {
  const { user } = useContext(AuthenticationContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>('');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  const handleBeginTest = async () => {
    const validate = validateRequiredFields(backflowJobItem, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    setLoading(true);

    const statusPatch = { status: 'in_progress', emp_id: user.id.toString() };

    // If offline, update cache only
    if (!isOnline) {
      const success = patchDevice(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, statusPatch, true);
      if (success) {
        setErrorMessage('');
        onSuccess();
      } else {
        setErrorMessage('Job group not cached. Please visit the job group page while online first.');
      }
      setLoading(false);
      return;
    }

    // If online, update server
    try {
      const jobItemUpdateData = { user_id: user.id.toString(), status: 'in_progress' };

      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, jobItemUpdateData);

      // Also update cache (mark as synced since server succeeded)
      patchDevice(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, statusPatch, false);

      setErrorMessage('');
      onSuccess();
    } catch (error: any) {
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        // Connection lost during save - fallback to cache (mark as needs sync)
        const success = patchDevice(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, statusPatch, true);
        if (success) {
          setErrorMessage('');
          onSuccess();
        }
      } else {
        setErrorMessage('Something went wrong beginning the test.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <OfflineWarningAlert isOnline={isOnline} />
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage || validationErrors} />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
          {user?.id ? (
            <Button variant="contained" onClick={handleBeginTest}>
              Begin Test
            </Button>
          ) : (
            <Typography variant="body1">You must be logged in to begin a test.</Typography>
          )}
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default BeginTestButton;
